// Option sources for the questionbank form. Subject pickers select LEAF
// subjects (type_c "subject") grouped under their subject-group rows —
// v3 useProgrammeSubjectSelect (withChildren) / useOrgSubjectSelect parity.
import { CrudFilter, useList } from '@refinedev/core';
import { useCallback, useMemo } from 'react';

import type { APMultiSelectOption, APMultiSelectOptionGroup } from '@/components/ap/form';

type JsonApiRow = { id?: string | number; attributes?: Record<string, any> };

const attrs = (row: JsonApiRow) => row?.attributes ?? (row as Record<string, any>) ?? {};

/** org_programmes rows for the district programme picker (option value =
 *  attributes.programme_id — v3 OrgProgrammeSelect parity) + the raw rows so
 *  the caller can resolve the selected org_programme record. */
export const useOrgProgrammes = (enabled: boolean) => {
  const { data, isLoading } = useList({
    resource: 'org_programmes',
    pagination: { pageSize: 50 },
    queryOptions: { enabled },
  });
  const rows = useMemo(() => data?.data ?? [], [data?.data]);
  const options = useMemo<APMultiSelectOption[]>(
    () =>
      rows.map((row: JsonApiRow) => {
        const item = attrs(row);
        return { value: item.programme_id, label: String(item.name ?? item.id) };
      }),
    [rows]
  );
  return { rows, options, isLoading };
};

const groupsFromChildren = (rows: JsonApiRow[]): APMultiSelectOptionGroup[] =>
  rows
    .map((row) => {
      const item = attrs(row);
      const children: any[] = item.children ?? [];
      return {
        label: String(item.name ?? item.label ?? ''),
        options: children.map((child) => ({
          value: child.id,
          label: String(child.name ?? child.label ?? child.id),
        })),
      };
    })
    .filter((group) => group.options.length > 0);

/** Async programme-subject picker props (non-district subject select; also
 *  the rubric leaf-subject picker). Spread into APFormCombobox: options load
 *  a LEAF page server-side and typing re-queries via `search eq` — a bounded
 *  first page can never hide a subject from search (the flat-fetch
 *  predecessor truncated at one page and searched only what it had loaded;
 *  the grouped-fetch variant capped at the group page size). Group headings
 *  are re-derived client-side from a light group-name map so duplicate leaf
 *  names (Language A vs B "Afrikaans") stay tellable apart. The picker
 *  selects LEAF ids (write contract:
 *  questionbank_programme_subjects.programme_subject_id); hydrated edit
 *  values label themselves through the combobox's getMany (by_ids) path. */
export const useProgrammeSubjectSearchSelect = (
  programmeId: string | number | undefined | null
) => {
  // Name map only — light rows (no should_serialize children payload), no
  // unarchived filter so a leaf under an archived group keeps its heading.
  const { data } = useList({
    resource: 'programme_subjects',
    filters: [
      { field: 'should_serialize', operator: 'eq', value: false },
      { field: 'by_type_c', operator: 'eq', value: 'group' },
      { field: 'by_programme_id', operator: 'eq', value: programmeId },
    ],
    pagination: { pageSize: 50 },
    queryOptions: { enabled: !!programmeId },
  });
  const groupNames = useMemo(() => {
    const names = new Map<string, string>();
    (data?.data ?? []).forEach((row: JsonApiRow) => {
      const item = attrs(row);
      if (item.id != null) names.set(String(item.id), String(item.name ?? item.label ?? ''));
    });
    return names;
  }, [data?.data]);

  // Group the current leaf page under its parents, in name-map (fetch) order;
  // leaves with an unknown/absent parent render as a heading-less run.
  const transformGroups = useCallback(
    (records: JsonApiRow[]): APMultiSelectOptionGroup[] => {
      const buckets = new Map<string, APMultiSelectOption[]>();
      records.forEach((row) => {
        const item = attrs(row);
        const key = item.parent_id != null ? String(item.parent_id) : '';
        const option = { value: item.id, label: String(item.name ?? item.label ?? item.id) };
        buckets.set(key, [...(buckets.get(key) ?? []), option]);
      });
      const groups: APMultiSelectOptionGroup[] = [];
      groupNames.forEach((label, id) => {
        const options = buckets.get(id);
        if (options) {
          groups.push({ label, options });
          buckets.delete(id);
        }
      });
      buckets.forEach((options) => groups.push({ options }));
      return groups;
    },
    [groupNames]
  );

  const comboProps = useMemo(
    () => ({
      resource: 'programme_subjects',
      filters: [
        { field: 'by_type_c', operator: 'eq', value: 'subject' },
        { field: 'by_programme_id', operator: 'eq', value: programmeId },
        { field: 'unarchived', operator: 'eq', value: true },
      ] as CrudFilter[],
      transformGroups: transformGroups as (records: any[]) => APMultiSelectOptionGroup[],
      pageSize: 25,
    }),
    [programmeId, transformGroups]
  );

  return { comboProps };
};

/** Org subjects, grouped (district picker for hs/ms/ps/fs programmes). */
export const useOrgSubjectGroupedOptions = (orgProgrammeId: string | number | undefined | null) => {
  const { data, isLoading } = useList({
    resource: 'subjects',
    filters: [
      { field: 'by_type_c', operator: 'eq', value: 'group' },
      { field: 'by_org_programme_id', operator: 'eq', value: orgProgrammeId },
    ],
    pagination: { pageSize: 15 },
    queryOptions: { enabled: !!orgProgrammeId },
  });
  const groups = useMemo(() => groupsFromChildren(data?.data ?? []), [data?.data]);
  return { groups, isLoading };
};
